import React, { useState } from 'react';
import { AlertCircle, Check, Image as ImageIcon, RefreshCw, Upload } from 'lucide-react';
import { SITE_IMAGE_SLOTS, SiteImageKey } from '../../assets/siteImages';
import { useSiteImages } from '../../context/SiteImagesContext';
import { ImagePickerModal } from './ImagePickerModal';
import { SafeImage } from '../SafeImage';

export const SiteImagesManager: React.FC = () => {
  const { images, databaseEngine, isLoading, loadError, refreshImages, saveImage } = useSiteImages();
  const [selectedKey, setSelectedKey] = useState<SiteImageKey | null>(null);
  const [savingKey, setSavingKey] = useState<SiteImageKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<SiteImageKey | null>(null);

  const handleSelectImage = async (imageUrl: string) => {
    if (!selectedKey) return;
    const key = selectedKey;
    setSavingKey(key);
    setError(null);
    setSavedKey(null);
    try {
      await saveImage(key, imageUrl);
      setSavedKey(key);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Could not save the image to the database.');
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-800 bg-slate-950/80 p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 shrink-0 rounded-xl bg-sky-500/10 border border-sky-500/20 text-sky-400 flex items-center justify-center">
              <ImageIcon className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white">Static Website Images</h2>
              <p className="mt-1 max-w-2xl text-xs leading-relaxed text-slate-400">
                Change the images used in the page banners and homepage hero. Assignments are saved through the server to the shared database, so visitors on other devices see the same images.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void refreshImages()}
            disabled={isLoading}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-300 transition-colors hover:bg-slate-800 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh from database
          </button>
        </div>
        {databaseEngine && (
          <div className={`mt-4 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold ${
            databaseEngine === 'mysql'
              ? 'border-emerald-800/60 bg-emerald-950/30 text-emerald-300'
              : 'border-amber-800/60 bg-amber-950/30 text-amber-200'
          }`}>
            <span className={`h-1.5 w-1.5 rounded-full ${databaseEngine === 'mysql' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
            {databaseEngine === 'mysql'
              ? 'Connected to MySQL'
              : 'Using server-side fallback storage — configure MySQL in production'}
          </div>
        )}
        {loadError && (
          <div role="alert" className="mt-4 flex items-start gap-2 rounded-xl border border-amber-800/60 bg-amber-950/30 p-3 text-xs leading-relaxed text-amber-200">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Could not load saved site images. The original images remain in use until the database is reachable. {loadError}</span>
          </div>
        )}
        {error && (
          <div role="alert" className="mt-4 flex items-start gap-2 rounded-xl border border-rose-800/60 bg-rose-950/30 p-3 text-xs leading-relaxed text-rose-200">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error} The previous saved image is still in use.</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {(Object.entries(SITE_IMAGE_SLOTS) as [SiteImageKey, (typeof SITE_IMAGE_SLOTS)[SiteImageKey]][]).map(([key, slot]) => (
          <article key={key} className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-950/80">
            <div className="relative aspect-[16/9] bg-slate-900">
              <SafeImage
                src={images[key]}
                alt={slot.label}
                fallbackCategory={slot.category === 'blog' ? 'blog' : 'general'}
                className="h-full w-full object-cover"
              />
              {savingKey === key && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-950/70 text-xs font-semibold text-white">
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Saving to database…
                </div>
              )}
              {savedKey === key && savingKey !== key && (
                <div className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-bold text-white shadow">
                  <Check className="h-3.5 w-3.5" /> Saved
                </div>
              )}
            </div>
            <div className="space-y-3 p-4">
              <div>
                <h3 className="text-sm font-bold text-white">{slot.label}</h3>
                <p className="mt-1 min-h-8 text-xs leading-relaxed text-slate-400">{slot.description}</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setSavedKey(null);
                  setSelectedKey(key);
                }}
                disabled={savingKey === key}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-sky-600 px-3 py-2.5 text-xs font-bold text-white transition-colors hover:bg-sky-500 disabled:opacity-50"
              >
                <Upload className="h-3.5 w-3.5" />
                Change image
              </button>
            </div>
          </article>
        ))}
      </div>

      {selectedKey && (
        <ImagePickerModal
          isOpen
          onClose={() => setSelectedKey(null)}
          currentImage={images[selectedKey]}
          onSelectImage={(imageUrl) => void handleSelectImage(imageUrl)}
          title={`Change ${SITE_IMAGE_SLOTS[selectedKey].label}`}
          categoryHint={SITE_IMAGE_SLOTS[selectedKey].category}
        />
      )}
    </div>
  );
};
